import React, { useEffect, useMemo, useRef } from "react";
import {
	SiteHeader,
	SiteFooter,
	type HeaderData,
	type FooterData,
	type LanguageOption,
	RendererRuntimeProvider
} from "@bcc/core-renderers";
import { PREVIEW_LOGO, PREVIEW_FOOTER_LOGO, type DisplayViewportKey } from "./previewUtils";
import { createRuntimeImageComponent, resolveStorageUrl } from "../../Helper/storage";

const VIEWPORT_WIDTHS: Record<DisplayViewportKey, number> = {
	mobile: 390,
	medium: 834,
	desktop: 1280
};

interface LayoutPreviewStripProps {
	mode: "header" | "footer";
	headerData?: HeaderData | null;
	footerData?: FooterData | null;
	language: string;
	languageOptions: LanguageOption[];
	defaultLanguageCode: string;
	viewport: DisplayViewportKey;
	title: string;
	description?: string;
	logoAltFallback?: string;
}

/** Isolated preview strip for the header or footer at a given viewport. */
const LayoutPreviewStrip: React.FC<LayoutPreviewStripProps> = ({
	mode,
	headerData,
	footerData,
	language,
	languageOptions,
	defaultLanguageCode,
	viewport,
	title,
	description,
	logoAltFallback = "Banque Centrale du Congo"
}) => {
	const dataAvailable = mode === "header" ? headerData : footerData;
	// Allow CSS to constrain mobile width; keep desktop fluid.
	const frameStyle = useMemo(() => {
		if (viewport === "mobile") {
			return { width: "100%" };
		}
		return { width: "100%", maxWidth: "100%" };
	}, [viewport]);
	const canvasStyle = useMemo(() => {

		if (mode === "header" && viewport === "mobile") {
			return { minHeight: 940 };
		}

		return {minHeight: 0};
	}, [mode, viewport]);
	const rootRef = useRef<HTMLDivElement | null>(null);
	const runtimeImageComponent = useMemo(() => createRuntimeImageComponent(), []);
	const runtimeConfig = useMemo(
		() => ({
			disableLinks: true,
			onLinkClick: () => {
				/* prevent navigation inside preview */
			},
			imageComponent: runtimeImageComponent,
			assetResolver: resolveStorageUrl
		}),
		[runtimeImageComponent]
	);

	// For header mobile preview strips, auto-open the mobile menu once,
	// but keep it scoped to this preview only.
	useEffect(() => {
		if (mode !== "header" || viewport !== "mobile" || !dataAvailable) {
			return;
		}
		const root = rootRef.current;
		if (!root) return;

		const button = root.querySelector<HTMLButtonElement>('button[aria-controls="mobile-menu"]');
		if (!button) return;

		const expanded = button.getAttribute("aria-expanded");
		if (expanded !== "true") {
			button.click();
		}
	}, [mode, viewport, dataAvailable]);

	let rootClassName = "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm mb-4";
	if (mode === "header" && viewport === "desktop") {
		// Ensure desktop header dropdown overlays everything below (including mobile previews).
		rootClassName += " relative z-[70]";
	} else if (mode === "header" && viewport === "mobile") {
		// Slightly lower so desktop header can sit above it when overlapping.
		rootClassName += " relative z-[50]";
	}

	return (
		<div className={rootClassName}>
			<div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-3">
				<div>
					<p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">{title}</p>
					{description && <p className="text-xs text-slate-500">{description}</p>}
				</div>
				<span className="text-xs font-mono text-slate-400">{VIEWPORT_WIDTHS[viewport]}px</span>
			</div>
			<div className="mt-3 layout-preview-surface">
				<div className="layout-preview-frame shadow-lg rounded-none" data-viewport={viewport} style={frameStyle}>
					<RendererRuntimeProvider value={runtimeConfig}>
						<div ref={rootRef} className="layout-preview-canvas" style={canvasStyle}>
							{mode === "header" ? (
								<div className="layout-preview-header">
									{dataAvailable ? (
										<SiteHeader
											data={dataAvailable as HeaderData}
											logoSrc={PREVIEW_LOGO}
											logoAlt={(dataAvailable as HeaderData).logoAlt}
											homeHref="/"
											language={language}
											languageOptions={languageOptions}
											defaultLanguageCode={defaultLanguageCode}
										/>
									) : (
										<div className="p-4 text-sm text-slate-500">Add navigation data to preview the header.</div>
									)}
								</div>
							) : (
								<div className="layout-preview-footer">
									{dataAvailable ? (
										<SiteFooter
											data={dataAvailable as FooterData}
											logoSrc={PREVIEW_FOOTER_LOGO}
											logoAlt={headerData?.logoAlt ?? logoAltFallback}
										/>
									) : (
										<div className="p-4 text-sm text-slate-500">Add footer blocks to preview the footer.</div>
									)}
								</div>
							)}
						</div>
					</RendererRuntimeProvider>
				</div>
			</div>
		</div>
	);
};

export default LayoutPreviewStrip;


